/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState, useEffect, useMemo, useId} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Flex,
    Text,
    ListItem,
    List,
    Box
} from '@chakra-ui/react'
import {ArrowRight2Icon, SearchIcon} from '../icons'
import {
    capitalize,
    boldString,
    getSessionJSONItem,
    setSessionJSONItem,
    clearSessionJSONItem
} from '../../utils/utils'
import useNavigation from '../../hooks/use-navigation'
import debounce from 'lodash/debounce'
import {RECENT_SEARCH_KEY, RECENT_SEARCH_LIMIT, RECENT_SEARCH_MIN_LENGTH} from '../../constants'
import {productUrlBuilder, searchUrlBuilder, categoryUrlBuilder} from '../../utils/url'
import {FormattedMessage} from 'react-intl'
import {trackEvent} from '../../tracking/analytics'
import useEffectEvent from '../../hooks/use-effect-event'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'
import Autocomplete, {$maxWidth} from '../autocomplete'
import Suggestion from './partials/suggestion'
import ProductSuggestion from './partials/product-suggestion'
import RecentSearch from './partials/recent-search'

const formatSuggestions = (locale, searchSuggestions, input) => {
    return {
        categorySuggestions: searchSuggestions?.categorySuggestions?.categories?.map(
            (suggestion) => {
                return {
                    type: 'category',
                    id: suggestion.id,
                    link: categoryUrlBuilder({id: suggestion.id}),
                    name: boldString(suggestion.name, capitalize(input))
                }
            }
        ),
        productSuggestions: searchSuggestions?.productSuggestions?.products
            ?.filter((product) => product.productName)
            ?.map((product) => {
                return {
                    type: 'product',
                    productId: product.productId,
                    name: boldString(product.productName, capitalize(input)),
                    link: productUrlBuilder(product, locale),
                    image: product.c_image
                }
            }),
        phraseSuggestions: searchSuggestions?.productSuggestions?.suggestedPhrases
            ?.map((phrase) => {
                return {
                    type: 'phrase',
                    name: boldString(phrase.phrase, capitalize(input)),
                    link: searchUrlBuilder(phrase.phrase)
                }
            })
            .slice(0, 2)
    }
}

/**
 * The SearchInput component is a stylized
 * text input made specifically for use in
 * the application header.
 * @param  {object} props
 * @param  {object} ref reference to the input element
 * @return  {React.ReactElement} - SearchInput component
 */
const Search = ({
    containerProps = {position: {lg: 'relative'}},
    formProps,
    parentContainerProps,
    dropdownProps,
    hideSearch,
    leftIcon,
    withSearchButton = false,
    searchBarPosition = 'Top',
    searchQuery: initialSearchQuery,
    relative = false,
    ...props
}) => {
    const id = useId()
    const {formatMessage} = useIntl()
    // the displayed value of the search input
    const [searchValue, setSearchValue] = useState(initialSearchQuery || '')
    // the debounced value, that is used to fetch suggestions
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '')
    const navigate = useNavigation()
    const searchSuggestion = useSearchSuggestions(
        {
            parameters: {
                q: searchQuery,
                limit: 3
            }
        },
        {
            enabled: searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH
        }
    )
    const searchInputRef = useRef()
    const recentSearches = getSessionJSONItem(RECENT_SEARCH_KEY)
    const {locale} = useIntl()
    const searchSuggestions = useMemo(
        () => formatSuggestions(locale, searchSuggestion.data, searchValue || ''),
        [locale, searchSuggestion.data, searchValue]
    )
    const autocompleteRef = useRef(null)

    // search input default value
    useEffect(() => {
        if (initialSearchQuery != null) {
            setSearchQuery(initialSearchQuery)
            setSearchValue(initialSearchQuery)
        }
    }, [initialSearchQuery])
    // search input tracking
    const onSearchQueryChange = useEffectEvent((searchQuery) => {
        trackEvent({
            event: 'search',
            search_bar_position: searchBarPosition,
            search_query: searchQuery
        })
    })
    useEffect(() => {
        if (searchQuery) {
            onSearchQueryChange(searchQuery)
        }
    }, [searchQuery, onSearchQueryChange])

    const searchSuggestionsAvailable =
        searchSuggestions &&
        (searchSuggestions?.categorySuggestions?.length ||
            searchSuggestions?.phraseSuggestions?.length ||
            searchSuggestions?.productSuggestions?.length)

    const saveRecentSearch = (searchText) => {
        // Get recent searches or an empty array if undefined.
        let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || []

        // Check if term is already in the saved searches
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase()
        })

        // Create a new array consisting of the search text and up to 4 other resent searches.
        // I'm assuming the order is newest to oldest.
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT)

        // Replace the save resent search with the updated value.
        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
    }

    // search suggestions are disabled as they are not yet adjusted for STOKKE project
    const debouncedSearch = useMemo(() => debounce(setSearchQuery, 300), [])

    const onSearchChange = async (e, {newValue: input = e.target.value} = {}) => {
        if (input.length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedSearch(input)
        }
    }

    const clearInput = () => {
        searchInputRef.current.blur()
        autocompleteRef.current?.setIsOpen?.(false)
    }

    const onSubmitSearch = (e) => {
        e.preventDefault()
        // Avoid blank spaces to be searched
        let searchText = searchValue.trim()
        // Avoid empty string searches
        if (searchText.length < 1) {
            return
        }

        saveRecentSearch(searchText)
        clearInput()
        navigate(searchUrlBuilder(searchText))
        hideSearch?.()
    }

    const closeAndNavigate = (link, trackingData) => {
        hideSearch?.()
        clearInput()
        autocompleteRef.current?.setIsOpen(false)

        trackEvent({
            event: 'search',
            search_query: searchValue,
            ...trackingData
        })

        if (link) {
            navigate(link)
        }
    }

    const onSearchInputChange = (e, extra) => {
        setSearchValue(extra?.newValue ?? e?.target.value ?? '')
        onSearchChange(e, extra)
        // shouldOpenPopover()
    }

    const deleteInput = () => {
        setSearchValue('')
        clearInput()
        autocompleteRef.current?.setIsOpen?.(false)
        searchInputRef.current.focus()
    }

    const groupedSuggestions = useMemo(() => {
        const groups = [
            {
                type: 'suggestions',
                title: formatMessage({
                    defaultMessage: 'Suggestions',
                    id: 'search.suggestions.title'
                }),
                hideTitle: true,
                suggestions: searchSuggestions?.phraseSuggestions?.map((value) => ({
                    type: 'suggestion',
                    value
                }))
            },
            {
                type: 'products',
                title: formatMessage({
                    defaultMessage: 'Products',
                    id: 'global.products'
                }),
                suggestions:
                    searchSuggestions?.productSuggestions?.length > 0
                        ? [
                              ...searchSuggestions.productSuggestions.map((value) => ({
                                  type: 'product',
                                  value
                              })),
                              {
                                  type: 'submit-search'
                              }
                          ]
                        : []
            }
        ].filter(({suggestions}) => suggestions?.length > 0)

        if (groups.length > 0) {
            return groups
        }

        if (recentSearches?.length > 0) {
            return [
                {
                    type: 'recent',
                    title: formatMessage({
                        defaultMessage: 'Recent Searches',
                        id: 'recent_searches.heading.recent_searches'
                    }),
                    titleProps: {
                        'data-testid': 'sf-suggestion-recent'
                    },
                    suggestions: [
                        ...recentSearches.map((value) => ({
                            type: 'recent',
                            value
                        })),
                        {type: 'clear-recent'} // Add clear recent searches option
                    ]
                }
            ]
        }

        return []
    }, [formatMessage, recentSearches, searchSuggestions])

    const productResultsCount = useMemo(
        () => searchSuggestions?.productSuggestions?.length ?? 0,
        [searchSuggestions]
    )

    const onSuggestionSelected = (event, {suggestion: {type, value: suggestion}}) => {
        switch (type) {
            case 'suggestion':
                closeAndNavigate(suggestion.link, {
                    search_bar_position: searchBarPosition,
                    flyout_section: 'Phrases',
                    flyout_selection: suggestion.name
                })
                break
            case 'product':
                closeAndNavigate(suggestion.link, {
                    search_bar_position: searchBarPosition,
                    flyout_section: 'Products',
                    flyout_selection: suggestion.name
                })
                break
            case 'submit-search':
                onSubmitSearch(event)
                break
            case 'recent':
                closeAndNavigate(searchUrlBuilder(suggestion))
                break
            case 'clear-recent':
                clearSessionJSONItem(RECENT_SEARCH_KEY)
                closeAndNavigate(false)
                break
        }
    }

    return (
        <Box
            display="block"
            width="full"
            maxWidth={!withSearchButton ? $maxWidth.reference : null}
            {...formProps}
            as="form"
            onSubmit={onSubmitSearch}
            data-testid="search-form"
        >
            <Flex
                justifyItems="center"
                gap={2}
                flexWrap={{base: 'wrap', md: 'nowrap'}}
                justifyContent="center"
                position={{md: 'relative'}}
                {...parentContainerProps}
            >
                <Autocomplete
                    id={id}
                    ref={autocompleteRef}
                    multiSection
                    label={formatMessage({
                        id: 'global.search',
                        defaultMessage: 'Search'
                    })}
                    hideLabel
                    initialValue={initialSearchQuery}
                    isLoading={searchSuggestion.isInitialLoading}
                    suggestions={groupedSuggestions}
                    renderAnnouncementLabel={() =>
                        formatMessage(
                            {
                                id: 'global.count_results_found',
                                defaultMessage: '{count} results found'
                            },
                            {
                                count: productResultsCount
                            }
                        )
                    }
                    getOptionLabel={() => null}
                    containerProps={containerProps}
                    inputProps={{
                        role: 'combobox',
                        autoComplete: 'off',
                        'data-testid': 'search-input',
                        value: searchValue,
                        onChange: onSearchInputChange,
                        onFocus: () => {
                            // As per design we only want to show the popover if the input is focused and we have recent searches saved
                            // or we have search suggestions available and have inputed some text (empty text in this scenario should show recent searches)
                            // Update: when used on plp the searchQuery has the current search query which causes the popup to open without focus
                            // so we need to check if the input is focused as well
                            if (
                                recentSearches?.length > 0 ||
                                (searchSuggestionsAvailable && searchValue.length > 0) ||
                                (!initialSearchQuery &&
                                    searchSuggestionsAvailable &&
                                    searchValue.length > 0)
                            ) {
                                autocompleteRef.current?.setIsOpen?.(true)
                            }
                        },
                        onBlur: () => autocompleteRef.current?.setIsOpen?.(false),
                        type: 'search',
                        ref: searchInputRef,
                        paddingLeft: 14,
                        paddingRight: 14,
                        // Styles to prevent autofocus search input on iOS
                        // https://www.youtube.com/watch?v=uYZl86TMQ4M
                        sx: {
                            '&::autofill': {
                                display: 'none'
                            },
                            '&::-webkit-autofill': {
                                display: 'none'
                            },
                            '&::-webkit-autofill-display': {
                                display: 'none'
                            }
                        },
                        ...props
                    }}
                    dropdownProps={{
                        'data-testid': 'sf-suggestion-popover',
                        ...(relative && {
                            paddingTop: 6,
                            position: 'static',
                            maxWidth: '100vw',
                            boxShadow: null,
                            zIndex: null,
                            padding: null,
                            left: null,
                            right: null
                        }),
                        ...dropdownProps
                    }}
                    groupTitleProps={{
                        textStyle: 'Regular XLarge',
                        ...(searchSuggestions?.phraseSuggestions?.length &&
                            searchSuggestions?.productSuggestions?.length && {
                                // simulate divider between the products and suggestions groups
                                marginTop: 6,
                                paddingTop: 6,
                                borderTop: '1px solid',
                                borderColor: 'stokkeGray.mid',
                                width: 'full'
                            })
                    }}
                    optionsListProps={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6
                    }}
                    optionProps={{padding: 0}}
                    renderInput={(inputProps) => (
                        <InputGroup maxWidth={$maxWidth.reference}>
                            {leftIcon && (
                                <InputLeftElement width="auto" paddingLeft={2} pointerEvents="none">
                                    <SearchIcon />
                                </InputLeftElement>
                            )}
                            <Input {...inputProps} />
                            {searchValue && (
                                <InputRightElement width="auto" paddingRight={2}>
                                    <Button variant="link" onClick={deleteInput} size="sm">
                                        <Text as="span" display={{base: 'none', lg: 'inline'}}>
                                            <FormattedMessage
                                                defaultMessage="Delete input"
                                                id="product_list.search.delete_input"
                                            />
                                        </Text>
                                        <Text as="span" display={{base: 'inline', lg: 'none'}}>
                                            <FormattedMessage
                                                defaultMessage="Delete"
                                                id="global.delete"
                                            />
                                        </Text>
                                    </Button>
                                </InputRightElement>
                            )}
                        </InputGroup>
                    )}
                    renderGroup={({groupIndex, ...props}) => {
                        switch (groupedSuggestions[groupIndex].type) {
                            case 'suggestions':
                                return (
                                    <ListItem
                                        {...props}
                                        padding={0}
                                        display="flex"
                                        flexDirection="column"
                                        gap={6}
                                    />
                                )
                            case 'products':
                                return (
                                    <ListItem
                                        {...props}
                                        padding={0}
                                        display="flex"
                                        flexDirection="column"
                                        gap={4}
                                        alignItems="start"
                                    />
                                )
                            case 'recent':
                                return <ListItem {...props} padding={0} />
                            default:
                                return <ListItem {...props} padding={0} />
                        }
                    }}
                    renderOptionsList={({groupIndex, ...props}) => {
                        switch (groupedSuggestions[groupIndex].type) {
                            case 'suggestions':
                                return (
                                    <List
                                        {...props}
                                        padding={0}
                                        display="flex"
                                        flexDirection="column"
                                        gap={6}
                                        data-testid="sf-suggestion"
                                    />
                                )
                            case 'products':
                                return (
                                    <List
                                        {...props}
                                        padding={0}
                                        display="flex"
                                        flexDirection="column"
                                        gap={4}
                                        width="100%"
                                    />
                                )
                            case 'recent':
                                return (
                                    <List
                                        {...props}
                                        padding={0}
                                        mx={'-16px'}
                                        display="flex"
                                        flexDirection="column"
                                    />
                                )
                        }
                        return <List {...props} />
                    }}
                    renderOption={({suggestion}) => {
                        switch (suggestion.type) {
                            case 'suggestion':
                                return (
                                    <Suggestion
                                        suggestion={suggestion.value}
                                        closeAndNavigate={closeAndNavigate}
                                        searchBarPosition={searchBarPosition}
                                    />
                                )
                            case 'product':
                                return (
                                    <ProductSuggestion
                                        suggestion={suggestion.value}
                                        closeAndNavigate={closeAndNavigate}
                                        searchBarPosition={searchBarPosition}
                                    />
                                )
                            case 'submit-search':
                                return (
                                    <Button
                                        variant="link"
                                        rightIcon={<ArrowRight2Icon boxSize={4} />}
                                        onMouseDown={
                                            onSubmitSearch
                                        } /*the onBlur function is otherwise executed beforehand (in chrome)*/
                                    >
                                        <FormattedMessage
                                            defaultMessage="View all"
                                            id="search.view_all"
                                        />
                                    </Button>
                                )
                            case 'recent':
                                return (
                                    <RecentSearch
                                        recentSearch={suggestion.value}
                                        closeAndNavigate={closeAndNavigate}
                                        searchBarPosition={searchBarPosition}
                                    />
                                )
                            case 'clear-recent':
                                return (
                                    <Button
                                        data-testid="sf-clear-search"
                                        id="clear-search"
                                        width="full"
                                        onMouseDown={() => {
                                            clearSessionJSONItem(RECENT_SEARCH_KEY)
                                            closeAndNavigate(false)
                                        }}
                                        variant="menu-link"
                                    >
                                        <Text fontWeight="400" color="blue.600" fontSize={'md'}>
                                            <FormattedMessage
                                                defaultMessage="Clear recent searches"
                                                id="recent_searches.action.clear_searches"
                                            />
                                        </Text>
                                    </Button>
                                )
                            default:
                                return null
                        }
                    }}
                    onSuggestionSelected={onSuggestionSelected}
                />

                {withSearchButton && (
                    <Button variant="solid" type="submit" width={{base: 'full', md: '400px'}}>
                        <FormattedMessage defaultMessage="Search" id="empty_cart.button.search" />
                    </Button>
                )}
            </Flex>
        </Box>
    )
}

Search.propTypes = {
    dropdownProps: PropTypes.object,
    containerProps: PropTypes.object,
    formProps: PropTypes.object,
    parentContainerProps: PropTypes.object,
    hideSearch: PropTypes.func,
    leftIcon: PropTypes.node,
    withSearchButton: PropTypes.bool,
    searchBarPosition: PropTypes.string,
    searchQuery: PropTypes.string,
    relative: PropTypes.bool
}

if (process.env.NODE_ENV !== 'production') {
    Search.displayName = 'SearchInput'
}

export default Search
